import { useMemo } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';

import { useSelector } from '../context/hooks';
import { selectStepIndex } from '../context/selectors';

export const Stepper = () => {
	const currentIndex = useSelector(selectStepIndex);
	const intl = useIntl();

	const navSteps = useMemo(
		() => [
			intl.formatMessage({
				id: 'ubp.calculator.topnav.usage',
				defaultMessage: 'Usage',
			}),
			intl.formatMessage({
				id: 'ubp.calculator.topnav.needs',
				defaultMessage: 'Needs',
			}),
			intl.formatMessage({
				id: 'ubp.calculator.topnav.estimate',
				defaultMessage: 'Estimate',
			}),
			intl.formatMessage({
				id: 'ubp.calculator.topnav.allowance',
				defaultMessage: 'Allowance',
			}),
			intl.formatMessage({
				id: 'ubp.calculator.topnav.summary',
				defaultMessage: 'Summary',
			}),
		],
		[intl],
	);

	return (
		<div css={stepperRowStyle}>
			{navSteps.map((navStep: string, index: number) => {
				return (
					<StepperItem
						key={`step${index}`}
						index={index}
						currentIndex={currentIndex}
						label={navStep}
						isLast={index === navSteps.length - 1}
					/>
				);
			})}
		</div>
	);
};

interface StepperItemProps {
	label: string;
	index: number;
	currentIndex: number;
	isLast?: boolean;
}

const StepperItem = (props: StepperItemProps) => {
	const { label, index, currentIndex, isLast } = props;
	const isPast = index < currentIndex;
	const isFuture = index > currentIndex;
	const isCurrent = index === currentIndex;
	const isFirst = index === 0;

	const leftColor = isFirst
		? 'transparent'
		: isCurrent || isPast
			? wacToken('color.primary.blue')
			: wacToken('color.neutral.light400');
	const rightColor = isLast
		? 'transparent'
		: isCurrent || isFuture
			? wacToken('color.neutral.light400')
			: wacToken('color.primary.blue');
	return (
		<div
			css={stepperItemCss}
			style={{
				backgroundImage: `linear-gradient(to right, ${leftColor} 0 50%, ${rightColor} 50% 100%)`,
			}}
		>
			<div css={stepperCircle} className={`active-${isPast || isCurrent}`}>
				{isPast ? (
					<svg
						xmlns="http://www.w3.org/2000/svg"
						version="1.2"
						viewBox="0 0 16 16"
						width="16"
						height="16"
					>
						<path
							fill="white"
							fillRule="evenodd"
							d="m6.75 9.83l-1.92-2.31-1.16 0.96 2.5 3q0.23 0.26 0.58 0.27 0.35-0.01 0.58-0.27l5-6-1.16-0.96z"
						/>
					</svg>
				) : (
					index + 1
				)}
			</div>
			<div className={`active-${isPast || isCurrent}`} css={stepperLabelStyle}>
				{label}
			</div>
		</div>
	);
};

const stepperItemCss = css({
	backgroundSize: '100% 1px',
	backgroundPosition: '0 8px',
	backgroundRepeat: 'no-repeat',
	display: 'flex',
	gap: token('space.100'),
	alignItems: 'center',
	flexDirection: 'column',
});

const stepperRowStyle = css({
	display: 'grid',
	gridAutoFlow: 'column',
	gridAutoColumns: '1fr',
	width: '100%',
	maxWidth: '600px',
	margin: 'auto',
});

const stepperCircle = css({
	backgroundColor: wacToken('color.neutral.light200'),
	color: wacToken('color.primary.black'),
	width: '16px',
	height: '16px',
	borderRadius: '50%',
	borderStyle: 'solid',
	borderWidth: '1px',
	borderColor: wacToken('color.neutral.light400'),
	fontSize: 10,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flex: '0 0 auto',
	['&.active-true']: {
		backgroundColor: wacToken('color.primary.blue'),
		borderColor: wacToken('color.primary.blue'),
		color: wacToken('color.neutral.light0'),
	},
});

const stepperLabelStyle = css({
	fontSize: '12px',
	color: wacToken('color.primary.black'),
	['&.active-true']: {
		color: wacToken('color.primary.blue'),
	},
});
